import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import {
  lockScreen,
  unlockScreen,
} from '../components/common/dialogs/modals/ScreenLock';
import { arrayOrArray } from '../utils/util';

export interface IButton {
  disabled: boolean;
  hidden: boolean;
  label: string;
  isLoading: boolean;
  title: string;
  variant?: string;
}

export type TButtonsState = {
  buttons: Record<string, IButton>;
  disabledActions: string[];
  hasLockedScreen: boolean;
  discouragedActions: string[];
  performingActions: string[];
  suggestedActions: string[];
  hiddenActions: string[];
};

export type TUploadActions = { action: string } & Partial<{
  disabled: boolean;
  suggested: boolean;
  performing: boolean;
  discouraged: boolean;
  hidden: boolean;
}>;

function checkShouldLock(state: Draft<TButtonsState>) {
  if (!state.hasLockedScreen && state.performingActions.length > 0) {
    state.hasLockedScreen = true;
    lockScreen(true);
  } else if (state.performingActions.length === 0) {
    state.hasLockedScreen = false;
    unlockScreen();
  }
}

const buttonsSlice = createSlice({
  initialState: {
    buttons: {},
    disabledActions: [],
    hasLockedScreen: false,
    discouragedActions: [],
    performingActions: [],
    suggestedActions: [],
    hiddenActions: [],
  } as TButtonsState,
  name: 'buttonsSlice',
  reducers: {
    addButton(state, { payload }: PayloadAction<IButton & { id: string }>) {
      state.buttons[payload.id] = payload;
    },

    reset(state) {
      state.disabledActions = [];
      state.disabledActions = [];
      state.suggestedActions = [];
      state.hiddenActions = [];
      state.hasLockedScreen = false;
      unlockScreen();
    },
    setState: (state, { payload }: PayloadAction<Partial<TButtonsState>>) => {
      Object.assign(state, payload);
      checkShouldLock(state);
    },
    updateButton(
      state,
      { payload }: PayloadAction<{ id: string } & Partial<IButton>>,
    ) {
      if (!state.buttons[payload.id]) {
        console.warn(`Button with id ${payload.id} does not exist`);
        return;
      }
      state.buttons[payload.id] = { ...state.buttons[payload.id], ...payload };
      checkShouldLock(state);
    },
    updateAction: (
      state,
      { payload }: PayloadAction<TUploadActions | TUploadActions[]>,
    ) => {
      arrayOrArray(payload).forEach((currentDefinition) => {
        if (currentDefinition.disabled !== undefined) {
          if (currentDefinition.disabled) {
            state.disabledActions.push(currentDefinition.action);
          } else {
            state.disabledActions = state.disabledActions.filter(
              (current) => current !== currentDefinition.action,
            );
          }
        }
        if (currentDefinition.suggested !== undefined) {
          if (currentDefinition.suggested) {
            state.suggestedActions.push(currentDefinition.action);
          } else {
            state.suggestedActions = state.suggestedActions.filter(
              (current) => current !== currentDefinition.action,
            );
          }
        }
        if (currentDefinition.performing !== undefined) {
          if (currentDefinition.performing) {
            state.performingActions.push(currentDefinition.action);
          } else {
            state.performingActions = state.performingActions.filter(
              (current) => current !== currentDefinition.action,
            );
          }
        }
        if (currentDefinition.discouraged !== undefined) {
          if (currentDefinition.discouraged) {
            state.discouragedActions.push(currentDefinition.action);
          } else {
            state.discouragedActions = state.discouragedActions.filter(
              (current) => current !== currentDefinition.action,
            );
          }
        }
        if (currentDefinition.hidden !== undefined) {
          if (currentDefinition.hidden) {
            state.hiddenActions.push(currentDefinition.action);
          } else {
            state.hiddenActions = state.hiddenActions.filter(
              (current) => current !== currentDefinition.action,
            );
          }
        }
      });
      checkShouldLock(state);
    },
  },
});

export const buttonsActions = buttonsSlice.actions;
export default buttonsSlice.reducer;
